import { FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import NoSlotsSummary from '../../components/NoSlots/NoSlotsSummary';
import { Separator } from '../../components/Separator/Separator';
import FeedbackWidget from '../../components/Typeform/FeedbackWidget';
import { StylesheetContext } from '../../contexts/StylesheetContext';
import { TicketContext } from '../../contexts/TicketContext';
import DateTime from '../../helpers/DateTime';
import { isTicketCancellable, isTicketReschedulable } from '../../helpers/TicketHelpers';
import { NoTopSlot } from '../../interfaces/NoTopSlot';
import { Ticket } from '../../interfaces/Ticket';
import { sendConfirmShown24Hours } from '../../services/Analytics';
import { useFormatting } from '../../services/hooks/useFormatting';
import usePageTitle from '../../services/hooks/usePageTitle';
import ImproveMeterkast from '../Meterkast/components/ImproveMeterkast/ImproveMeterkast';
import { AboutAppointment, AboutAppointmentContent } from './AboutAppointment/AboutAppointment';
import styles from './Confirm.module.scss';
import { ConfirmCard } from './ConfirmCard/ConfirmCard';
import ConfirmReschedule from './ConfirmReschedule';
import ExpertCard from './ExpertCard/ExpertCard';


const Confirm: FC = () => {
  const { stylesheet } = useContext(StylesheetContext);
  const { ticket, showFeedback } = useContext(TicketContext);
  const { state } = useLocation();
  const { t } = useTranslation();
  const lastLoggedRef = useRef<boolean>(false);
  const selectedNoTopSlots: NoTopSlot[] | undefined = useMemo(() => state?.selectedNoTopSlots, [state]);

  usePageTitle(t('pageTitle:confirm'));

  const within24hours = useMemo(() => {
    const start = ticket?.workOrder.appointment?.arrivalWindowStart;

    return start ? DateTime.withinDayBefore(start) : false;
  }, [ticket?.workOrder.appointment?.arrivalWindowStart]);

  useEffect(() => {
    if (within24hours && !lastLoggedRef.current) {
      sendConfirmShown24Hours();
      lastLoggedRef.current = true;
    }
  }, [within24hours]);

  const appointmentDurationRange = useMemo(() => {
    const appointment = ticket?.workOrder.appointment;

    if (!appointment?.scheduledStart || !appointment?.scheduledEnd) {
      return undefined;
    }

    return {
      start: appointment.scheduledStart,
      finish: appointment.scheduledEnd
    };
  }, [ticket?.workOrder.appointment]);

  const { formattedText: averageTime } = useFormatting(t('confirmSection:averageVisit'), {
    replaceDate: ticket?.workOrder.appointment.arrivalWindowStart,
    range: appointmentDurationRange,
    roundTo: 5
  });

  const { formattedText: warningText } = useFormatting(stylesheet.confirmationSection.warningText);

  const getExpertTitle = useCallback((ticket: Ticket): string => {
    switch (ticket.workOrder.appointment.status) {
      case 'Cannot Complete':
      case 'Completed':
      case 'In Progress':
        return t('confirmSection:whoWasThere');
      default:
        return t('confirmSection:whosComing');
    }
  }, [t]);

  const getAboutAppointmentData = useCallback((
    contentTitle: string,
    moreThan24HoursBefore: boolean
  ): AboutAppointmentContent[] => {
    const base: AboutAppointmentContent[] = [];

    if (moreThan24HoursBefore) {
      base.push({ icon: 'info', title: contentTitle, description: warningText, iconColor: '#FFAC4B' });
    }

    base.push({ icon: 'schedule', title: contentTitle, description: averageTime });

    return base;
  }, [averageTime, warningText]);

  const shouldDisplaySelectedNoTopSlots = !!selectedNoTopSlots?.length;

  if (!ticket || !stylesheet.confirmationSection) {
    // Should never happen, ErrorElement will be rendered instead
    return null;
  }

  const feedbackQuestionsId = stylesheet.settings.feedbackQuestions?.id;
  const serviceAppointmentId = ticket.workOrder.appointment?.id || '';
  const appointment = ticket.workOrder.appointment;
  const moreThan24HoursBefore = appointment.arrivalWindowStart ?
    DateTime.moreThanDayBefore(appointment.arrivalWindowStart) : false;

  const lessThan24HoursAfter = appointment.scheduledEnd ? DateTime.lessThanDayAfter(appointment.scheduledEnd) : false;

  const reschedulingEnabled = isTicketReschedulable(ticket);
  const cancelingEnabled = isTicketCancellable(ticket, stylesheet);

  return (
    <div className={styles.ticketConfirm}>
      <ConfirmCard shouldDisplaySelectedNoTopSlots={shouldDisplaySelectedNoTopSlots} />

      <ImproveMeterkast />

      <div className={styles.confirmContent}>
        <Separator />

        {shouldDisplaySelectedNoTopSlots && (
          <div className={styles.timeSlotContainer}>
            <NoSlotsSummary selectedNoTopSlots={selectedNoTopSlots} />
          </div>
        )}

        {lessThan24HoursAfter && <AboutAppointment
          content={getAboutAppointmentData(stylesheet.confirmationSection.contentTitle, moreThan24HoursBefore)}
          title={t('confirmSection:aboutYourVisit')}
        />}

        {(reschedulingEnabled || cancelingEnabled) && (
          <>
            <Separator />
            <ConfirmReschedule
              availabilityProvided={shouldDisplaySelectedNoTopSlots}
              cancelingEnabled={cancelingEnabled}
              hashId={ticket.hashId}
              reschedulingEnabled={reschedulingEnabled}
            />
          </>
        )}

        {within24hours && !!appointment.expertId && lessThan24HoursAfter && (
          <>
            <Separator />
            <div className={styles.visitcardContainer}>
              <h2 className={styles.expertTitle}>{getExpertTitle(ticket)}</h2>
              <ExpertCard ticketId={ticket.hashId} />
            </div>
          </>
        )}
        {showFeedback && !!feedbackQuestionsId && !!serviceAppointmentId && (
          <>
            <Separator />
            <div className={styles.visitcardContainer}>
              <h2 className={styles.feedbackTitle}>{t('confirmSection:feedbackTitle')}</h2>
              <FeedbackWidget feedbackQuestionsId={feedbackQuestionsId} serviceAppointmentId={serviceAppointmentId} />
            </div>
          </>

        )}
      </div>
    </div>
  );
};

export default Confirm;
